/**
 * Profile table schema using the general table component
 * Includes profile cross-section preview in the last column
 */

import { renderProfilePreview } from "./profilePreviewCell";
import type { CellRenderContext, TableColumn, TableData } from "./tableSchema";
import { Align, drawTable, registerCellRenderer } from "./tableSchema";
import { PDF_TEXT_SIZE, PDF_TITLE_SIZE } from "./template";
import type { PdfProfileEntry } from "./types";

// Register the profile preview renderer
registerCellRenderer(
  "profilePreview",
  async (
    value: unknown,
    cellX: number,
    cellY: number,
    cellWidth: number,
    cellHeight: number,
    context: CellRenderContext,
  ) => {
    // value should be the crossSection SVG path string
    if (typeof value === "string" && value.length > 0) {
      // Extract position from rowData (5th element, index 4, stored as hidden data)
      const position = context.rowData[4] as string | undefined;
      renderProfilePreview({
        svgPath: value,
        cellX,
        cellY,
        cellWidth,
        cellHeight,
        page: context.page,
        rgb: context.rgb,
        position, // Pass position to determine which half to fill
      });
    }
  },
);

// =============================================================================
// PROFILE TABLE CONVERSION
// =============================================================================

/**
 * Convert profile entries to general table format
 */
function convertProfilesToTableData(profiles: PdfProfileEntry[]): TableData {
  const columns: TableColumn[] = [
    { name: "Anz", widthPct: 0.12, align: Align.Center },
    { name: "×", widthPct: 0.08, align: Align.Center },
    { name: "Länge (mm)", widthPct: 0.35, align: Align.Center },
    {
      name: "Profil",
      widthPct: 0.45,
      align: Align.Center,
      rendererType: "profilePreview", // Use registered renderer
    } satisfies TableColumn,
  ];

  const rows = profiles.map((profile) => [
    profile.count,
    "×", // Multiplication sign
    profile.length,
    (profile as PdfProfileEntry & { crossSection?: string }).crossSection ?? "", // Pass SVG path to renderer
    // Store position as hidden 5th element for the renderer to access
    profile.position,
  ]);

  return {
    columns,
    rows,
    rowHeight: 7, // mm
    fontSize: {
      header: PDF_TITLE_SIZE, // Use central font size definition
      body: PDF_TEXT_SIZE, // Use central font size definition
    },
    showHeader: false, // No header row
  };
}

/**
 * Draw the profiles table using the general table component
 */
async function drawProfileTable(
  arg: {
    pdfDoc: import("pdf-lib").PDFDocument;
    page: import("pdf-lib").PDFPage;
    schema: {
      position: { x: number; y: number };
      width: number;
      height: number;
    };
    value: string;
  },
  rgb: (r: number, g: number, b: number) => import("pdf-lib").RGB,
  StandardFonts: typeof import("pdf-lib").StandardFonts,
): Promise<void> {
  const { value } = arg;

  // Parse profile data
  let profiles: PdfProfileEntry[] = [];
  try {
    if (value && typeof value === "string") {
      profiles = JSON.parse(value);
    } else {
      return;
    }
  } catch (e) {
    console.warn("Failed to parse profile data:", e);
    return;
  }

  if (profiles.length === 0) return;

  // Convert to general table format
  const tableData = convertProfilesToTableData(profiles);

  // Use general table drawer
  await drawTable(
    {
      ...arg,
      value: JSON.stringify(tableData),
    },
    rgb,
    StandardFonts,
  );
}

// =============================================================================
// SCHEMA DEFINITION
// =============================================================================

export const profileTableSchema = {
  ui: (arg: { rootElement: HTMLElement; value: string }) => {
    const div = document.createElement("div");
    div.style.width = "100%";
    div.style.height = "100%";
    div.style.backgroundColor = "#f5f5f5";
    div.style.display = "flex";
    div.style.alignItems = "center";
    div.style.justifyContent = "center";
    div.style.fontSize = "10px";
    div.style.color = "#666";
    div.textContent = "Profile Table Preview";
    arg.rootElement.appendChild(div);
  },

  pdf: async (arg: {
    pdfDoc: import("pdf-lib").PDFDocument;
    page: import("pdf-lib").PDFPage;
    schema: {
      position: { x: number; y: number };
      width: number;
      height: number;
    };
    value: string;
  }) => {
    const { rgb, StandardFonts } = await import("pdf-lib");
    await drawProfileTable(arg, rgb, StandardFonts);
  },

  propPanel: {
    schema: {},
    defaultSchema: {
      name: "profilesTable",
      type: "profileTable",
      position: { x: 0, y: 0 },
      width: 190,
      height: 60,
    },
    defaultValue: "[]",
  },
};

export default profileTableSchema;
